export type ExternalUrlStatus = "empty" | "invalid" | "ok";

export interface ParsedExternalUrl {
  /** Navigable absolute URL, or null when it must not be rendered as a link. */
  href: string | null;
  status: ExternalUrlStatus;
  /** Ready-to-show pt-BR copy explaining what is wrong. Null when nothing is. */
  warning: string | null;
  /** Original stored value, so the operator can see what needs fixing. */
  raw: string;
}

/** Characters that cannot appear in a real URL path and mark a template placeholder. */
const PLACEHOLDER_CHARS = /[<>{}[\]|"\\^`]/;

/** Slugs that are obviously the template's example rather than a real profile. */
const PLACEHOLDER_SLUGS = new Set([
  "profile",
  "perfil",
  "seu-perfil",
  "seuperfil",
  "seu_perfil",
  "usuario",
  "username",
  "user",
  "nome-sobrenome",
  "nomesobrenome",
  "exemplo",
  "example",
]);

/** LinkedIn paths that carry a profile identifier in their second segment. */
const LINKEDIN_PROFILE_PREFIXES = new Set(["in", "company", "school", "pub"]);

function emptyResult(raw: string): ParsedExternalUrl {
  return { href: null, status: "empty", warning: null, raw };
}

function invalidResult(raw: string, warning: string): ParsedExternalUrl {
  return { href: null, status: "invalid", warning, raw };
}

/**
 * Best-effort repair of a hand-typed URL: registration data arrives with text
 * prefixes ("Meu Linkedin: https://..."), missing colons ("Http//host"), missing
 * protocol and trailing junk after a space.
 */
function sanitize(raw: string): string {
  let candidate = raw;

  const protocolIndex = candidate.toLowerCase().indexOf("http");
  if (protocolIndex > 0) candidate = candidate.slice(protocolIndex);

  candidate = candidate.replace(/^(https?)\/\//i, "$1://");
  if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;

  return candidate.split(/\s/)[0];
}

function hasRealTld(hostname: string): boolean {
  return /\.[a-z]{2,}$/i.test(hostname.replace(/\.$/, ""));
}

function decodePath(pathname: string): string {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

/**
 * Normalizes and validates an external URL, distinguishing "not filled in" from
 * "filled in with something unusable" so callers can show them differently. A
 * null `href` means the value must never become an `<a href>`.
 */
export function parseExternalUrl(url: string | null | undefined): ParsedExternalUrl {
  const raw = (url ?? "").trim();
  if (!raw) return emptyResult(raw);

  // Checked before parsing: new URL() percent-encodes "<profile>" into "%3Cprofile%3E".
  if (PLACEHOLDER_CHARS.test(raw)) {
    return invalidResult(raw, "Link inválido: parece um exemplo, não um endereço real");
  }

  let parsed: URL;
  try {
    parsed = new URL(sanitize(raw));
  } catch {
    return invalidResult(raw, "Link inválido: não é um endereço válido");
  }

  if (!hasRealTld(parsed.hostname)) {
    return invalidResult(raw, "Link inválido: o endereço não tem um domínio válido");
  }
  // An email pasted into a URL field parses as userinfo, not as a host.
  if (parsed.username || parsed.password) {
    return invalidResult(raw, "Link inválido: não é um endereço válido");
  }
  if (PLACEHOLDER_CHARS.test(decodePath(parsed.pathname))) {
    return invalidResult(raw, "Link inválido: parece um exemplo, não um endereço real");
  }

  return { href: parsed.toString(), status: "ok", warning: null, raw };
}

function linkedInSlug(parsed: URL): { prefix: string; slug: string } | null {
  const segments = decodePath(parsed.pathname)
    .split("/")
    .filter((segment) => segment.trim() !== "");
  // /mwlite/in/<slug> é o mobile-lite: perfil real, só com um segmento a mais.
  if (segments[0]?.toLowerCase() === "mwlite") segments.shift();
  if (segments.length === 0) return null;
  const [prefix, slug] = segments;
  return { prefix: prefix.toLowerCase(), slug: slug ?? "" };
}

/**
 * Same as `parseExternalUrl`, plus the rules that only make sense for a field
 * that is supposed to hold a LinkedIn profile. A working URL from another site
 * stays clickable and is only flagged, since hiding it would remove access the
 * operator has today.
 */
export function parseLinkedInUrl(url: string | null | undefined): ParsedExternalUrl {
  const parsedUrl = parseExternalUrl(url);
  if (parsedUrl.status !== "ok" || !parsedUrl.href) {
    if (parsedUrl.status === "invalid") {
      return { ...parsedUrl, warning: parsedUrl.warning ?? "Link do LinkedIn inválido" };
    }
    return parsedUrl;
  }

  const parsed = new URL(parsedUrl.href);
  const isLinkedInHost = /(^|\.)linkedin\.com$/i.test(parsed.hostname);
  if (!isLinkedInHost) {
    return { ...parsedUrl, warning: "Este link não é do LinkedIn" };
  }

  // No domínio do LinkedIn o valor tem que ser um perfil de verdade: /feed/ e
  // /jobs/view/... abrem, mas não são o dado que o campo deveria guardar.
  const path = linkedInSlug(parsed);
  if (!path || !LINKEDIN_PROFILE_PREFIXES.has(path.prefix)) {
    return invalidResult(parsedUrl.raw, "Link do LinkedIn inválido: não é um perfil");
  }
  if (!path.slug) {
    return invalidResult(parsedUrl.raw, "Link do LinkedIn sem perfil: falta o nome do perfil");
  }
  if (PLACEHOLDER_SLUGS.has(path.slug.toLowerCase())) {
    return invalidResult(
      parsedUrl.raw,
      "Link do LinkedIn inválido: parece um exemplo, não um perfil real",
    );
  }

  return parsedUrl;
}

/**
 * Navigable URL or null, for the places that need a bare string instead of the
 * full result: iframe `src`, clipboard text, batch "open all" queues.
 */
export function externalUrlHref(url: string | null | undefined): string | null {
  return parseExternalUrl(url).href;
}

/**
 * Opens an external URL in a new tab, only when it is navigable. Returns false
 * without side effects otherwise, so callers can report what was skipped.
 */
export function openExternalUrl(url: string | null | undefined): boolean {
  const { href } = parseExternalUrl(url);
  if (!href) return false;
  window.open(href, "_blank", "noopener,noreferrer");
  return true;
}
